
import React, { useState, useMemo } from "react";
import { useTranslation } from "../i18n";
import "../styles/NewsletterForm.css";
import MainInput from "./MainInput";
import PrivacyPolicy from "./PrivacyPolicy";
import { getCountryOptions } from "./functions/countryList";

const NewsletterForm = ({
  setShowForm,
  setSuccessModal,
  onSubmit,

  preferredContact,
  email,
  phone,
  cpf,
  fullName,
  birthDate,
  cityState,

  onPreferredContactChange,
  onEmailChange,
  onPhoneChange,
  onCpfChange,
  onFullNameChange,
  onBirthDateChange,
  onCityStateChange,
  onCitySuggestionSelect,
  instagram,
  onInstagramChange,
  nationality,
  onNationalityChange,
  attendedEvent,
  setAttendedEvent,

  citySuggestions,

  isSubmitting,
  submitError,
  setSubmitError,
}) => {
  const { t, lang } = useTranslation();

  const countryOptions = useMemo(() => getCountryOptions(lang), [lang]);

  const handleSubmit = async (e) => {
    const result = await onSubmit(e);
    if (result && result.ok) {
      setShowForm(false);
      setSuccessModal({ show: true, isNew: result.isNew });
    }
  };

  const handleCitySuggestionClick = (label) => {
    onCitySuggestionSelect(label);
  };

  const [consent, setConsent] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const contactOptions = [
    t("form.contactEmail"),
    t("form.contactWhatsApp"),
    t("form.contactInstagram"),
  ];

  return (
    <div className="newsletter-overlay">
      <div className="form-container">

        {/* noValidate: all validation now routes through onSubmit's JS checks
            (styled .submit-error banner) instead of the browser's native
            required-field tooltips, so every field reports errors the same
            way regardless of which one is empty. */}
        <form onSubmit={handleSubmit} className="form" noValidate>
        <h2>{t("form.title")}</h2>
        <ul className="form-benefits">
          <li>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V7z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M14 5v14" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.5 2.5"/></svg>
            <span>{t("form.benefit1")}</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3a5 5 0 0 0-5 5v3.2c0 .6-.2 1.1-.6 1.6L5 14.5c-.7.8-.1 2 .9 2h12.2c1 0 1.6-1.2.9-2l-1.4-1.7c-.4-.5-.6-1-.6-1.6V8a5 5 0 0 0-5-5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M9.5 18.5a2.5 2.5 0 0 0 5 0" fill="none" stroke="currentColor" strokeWidth="1.6"/></svg>
            <span>{t("form.benefit2")}</span>
          </li>
        </ul>
        <p className="form-update-hint">{t("form.updateHint")}</p>

          <MainInput
            label={t("form.nameLabel")}
            type="text"
            value={fullName}
            onChange={onFullNameChange}
          />

          <MainInput
            label={t("form.birthLabel")}
            type="text"
            value={birthDate}
            onChange={onBirthDateChange}
            placeholder={t("form.birthPlaceholder")}
          />

          <MainInput
            label={t("form.nationalityLabel")}
            as="select"
            value={nationality}
            onChange={onNationalityChange}
            options={countryOptions}
          />

          {nationality === "BR" && (
            <MainInput
              label={t("form.cpfLabel")}
              type="text"
              value={cpf}
              onChange={onCpfChange}
            />
          )}

          <div className="city-autocomplete">
            <MainInput
              label={t("form.cityLabel")}
              type="text"
              value={cityState}
              onChange={onCityStateChange}
            />

            {citySuggestions && citySuggestions.length > 0 && (
              <ul className="city-suggestions">
                {citySuggestions.map((s) => (
                  <li
                    key={s}
                    onClick={() => handleCitySuggestionClick(s)}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <MainInput
              label={t("form.contactLabel")}
              as="select"
              value={preferredContact}
              onChange={onPreferredContactChange}
              options={contactOptions}
            />

          {preferredContact === "Instagram DM" && (
            <MainInput
              label={t("form.instagramLabel")}
              type="text"
              value={instagram}
              onChange={onInstagramChange}
              placeholder="@seuuser"
            />
          )}

          <MainInput
            label={t("form.emailLabel")}
            type="email"
            value={email}
            onChange={onEmailChange}
            placeholder="nome@email.com"
          />

          <MainInput
            label={t("form.phoneLabel")}
            type="tel"
            value={phone}
            onChange={onPhoneChange}
            placeholder={t("form.phonePlaceholder")}
          />

          <div className="attended-event-toggle">
            <span className="attended-event-question">{t("form.attendedEvent")}</span>
            <div className="attended-event-options">
              <label className="consent-label">
                <input
                  type="radio"
                  name="attendedEvent"
                  checked={attendedEvent === true}
                  onChange={() => setAttendedEvent(true)}
                />
                <span>{t("form.attendedEventYes")}</span>
              </label>
              <label className="consent-label">
                <input
                  type="radio"
                  name="attendedEvent"
                  checked={attendedEvent === false}
                  onChange={() => setAttendedEvent(false)}
                />
                <span>{t("form.attendedEventNo")}</span>
              </label>
            </div>
          </div>

          <div className="consent-wrapper">
            <label className="consent-label">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                {t("form.consent")}{" "}
                <button
                  type="button"
                  className="privacy-link"
                  onClick={() => setShowPrivacy(true)}
                >
                  {t("form.privacyLink")}
                </button>
              </span>
            </label>
          </div>

          {submitError && (
            <div className="submit-error">
              <span>⚠️ {submitError}</span>
              <button
                type="button"
                className="error-dismiss"
                onClick={() => setSubmitError(null)}
              >
                ✕
              </button>
            </div>
          )}

          <div className="btnWrapper">
            <button
              type="submit"
              className="main-button"
              disabled={!consent || isSubmitting}
            >
              {isSubmitting ? t("form.submitting") : t("form.submit")}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="main-button"
            >
              {t("form.cancel")}
            </button>
          </div>

          <p className="form-contact-us">
            {t("form.contactUs")}{" "}
            <a href="mailto:mixordiamusic@gmail.com">mixordiamusic@gmail.com</a>
          </p>
        </form>
      </div>

      {showPrivacy && (
        <PrivacyPolicy onClose={() => setShowPrivacy(false)} />
      )}
    </div>
  );
};

export default NewsletterForm;
